import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Clock, Calendar, User } from "lucide-react";
import { ScrollReveal } from "@/components/ScrollReveal";
import { blogPosts } from "@/data/blog";
import SEOHead from "@/components/SEOHead";

export default function BlogPostPage() {
  const { slug } = useParams();
  const postIndex = blogPosts.findIndex((p) => p.slug === slug);
  const post = blogPosts[postIndex];

  if (!post) return <Navigate to="/blog" replace />;

  const nextPost = blogPosts[(postIndex + 1) % blogPosts.length];
  const prevPost = blogPosts[(postIndex - 1 + blogPosts.length) % blogPosts.length];

  return (
    <main data-testid="blog-post-page">
      <SEOHead
        title={`${post.title} — Venury Blog`}
        description={post.excerpt}
        url={`https://venury.tech/blog/${slug}`}
        type="article"
      />
      {/* Hero */}
      <section className="relative pt-32 pb-12 overflow-hidden">
        <div className="absolute inset-0 bg-[#0B1116]" />
        <div className="hero-orb-teal" style={{ top: "-20%", right: "-10%" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#0B1116]" />

        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-6">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 font-mono text-sm text-slate-400 hover:text-teal-bright transition-colors duration-200"
            >
              <ArrowLeft size={14} /> Back to Blog
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="flex flex-wrap items-center gap-3 mb-6"
          >
            <span className="font-mono text-[10px] uppercase tracking-wider bg-teal-primary/20 text-teal-bright px-3 py-1 rounded">
              {post.category}
            </span>
            <div className="flex items-center gap-4 text-xs text-slate-600">
              <span className="flex items-center gap-1"><Clock size={12} /> {post.readTime}</span>
              <span className="flex items-center gap-1">
                <Calendar size={12} />
                {new Date(post.date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
              </span>
            </div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.15] tracking-tight"
          >
            {post.title}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="flex items-center gap-3 mt-8"
          >
            <div className="w-10 h-10 rounded-full bg-teal-primary/20 flex items-center justify-center">
              <User size={18} className="text-teal-bright" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white">{post.author}</p>
              <p className="text-xs text-slate-600">Venury Engineering Team</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="pb-12">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="rounded-lg overflow-hidden border border-white/5"
          >
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-64 md:h-96 object-cover"
            />
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            {post.content.map((paragraph, i) => (
              <ScrollReveal key={i} delay={i * 0.05}>
                <p className="text-base md:text-lg text-slate-400 leading-[1.8] mb-8">
                  {paragraph}
                </p>
              </ScrollReveal>
            ))}
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 pt-8 mt-8 border-t border-white/5">
            {post.tags.map((tag) => (
              <span key={tag} className="font-mono text-xs text-slate-400 px-3 py-1.5 rounded border border-white/5 bg-white/[0.02]">
                {tag}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Post Navigation */}
      <section className="py-12 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link
              to={`/blog/${prevPost.slug}`}
              data-testid="prev-post-link"
              className="group p-6 rounded-lg border border-white/5 hover:border-teal-primary/20 transition-colors duration-300"
            >
              <span className="font-mono text-xs text-slate-600 uppercase tracking-wider flex items-center gap-1 mb-2">
                <ArrowLeft size={12} /> Previous
              </span>
              <h3 className="font-heading text-base font-semibold text-white group-hover:text-teal-bright transition-colors duration-200 leading-snug">
                {prevPost.title}
              </h3>
            </Link>
            <Link
              to={`/blog/${nextPost.slug}`}
              data-testid="next-post-link"
              className="group p-6 rounded-lg border border-white/5 hover:border-teal-primary/20 transition-colors duration-300 text-right"
            >
              <span className="font-mono text-xs text-slate-600 uppercase tracking-wider flex items-center gap-1 justify-end mb-2">
                Next <ArrowRight size={12} />
              </span>
              <h3 className="font-heading text-base font-semibold text-white group-hover:text-teal-bright transition-colors duration-200 leading-snug">
                {nextPost.title}
              </h3>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
